/**
 * Site toasts, styled by components/notices.scss in www-prokonzulta-redesign.
 *
 * showToast({ type, title, text?, action?: { label, href }, duration?, onClose? })
 * - type: 'error' | 'warning' | 'success' | 'info'
 * - duration in ms; 0 keeps the toast until closed. Without it errors and warnings close after 8 s, the rest after 5 s.
 */
const TYPES = ['error', 'warning', 'success', 'info'];
const AUTO_CLOSE_MS = 5000;
// Errors and warnings get longer to be read.
const AUTO_CLOSE_ERROR_MS = 8000;
// Length of the site-toast-out animation in notices.scss.
const LEAVE_MS = 200;
const CLOSE_LABEL = 'Zavřít';
// Every webpack entry bundles its own copy of this module, so an open toast is found in the shared
// container and restarted through an event, not through module state.
const RESHOW_EVENT = 'site-toast:reshow';

// Lives directly in <body>: the header gets a transform on scroll, which would become
// the containing block of a fixed element inside it.
function container() {
    let root = document.querySelector('.site-toasts');
    if (!root) {
        root = element('div', 'site-toasts');
        root.setAttribute('aria-live', 'polite');
        document.body.appendChild(root);
    }
    return root;
}

function element(tag, className, text) {
    const el = document.createElement(tag);
    el.className = className;
    if (text) {
        el.textContent = text;
    }
    return el;
}

function render({ type, title, text, action, timeout }) {
    const toast = element('div', `site-toast site-toast--${type}`);
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    const icon = element('span', 'site-toast__icon');
    icon.setAttribute('aria-hidden', 'true');

    const body = element('div', 'site-toast__body');
    body.appendChild(element('div', 'site-toast__title', title));
    if (text) {
        body.appendChild(element('div', 'site-toast__text', text));
    }
    if (action) {
        const link = element('a', 'site-toast__action', action.label);
        link.href = action.href;
        body.appendChild(link);
    }

    const closeButton = element('button', 'site-toast__close', '×');
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', CLOSE_LABEL);

    toast.append(icon, body, closeButton);
    if (timeout) {
        const bar = element('span', 'site-toast__bar');
        bar.style.animationDuration = `${timeout}ms`;
        toast.appendChild(bar);
    }
    return { toast, closeButton };
}

// A toast shown again is re-inserted, which makes screen readers announce it again;
// the entry animation and the time bar have to be restarted by hand.
function replayAnimations(toast) {
    const animated = [toast, ...toast.querySelectorAll('.site-toast__bar')];
    animated.forEach((el) => { el.style.animationName = 'none'; });
    void toast.offsetWidth;
    animated.forEach((el) => { el.style.animationName = ''; });
}

export function showToast({ type, title, text, action, duration, onClose } = {}) {
    const kind = TYPES.includes(type) ? type : 'info';

    // The same message raised again (by another component, or by a retry that failed the same way)
    // reuses the open toast and shows it anew, so it is noticed again.
    const key = JSON.stringify([kind, title, text]);
    const open = [...container().children].find((el) => el.dataset.key === key && !el.classList.contains('is-leaving'));
    if (open) {
        open.dispatchEvent(new Event(RESHOW_EVENT));
        return open;
    }

    const timeout = duration ?? (kind === 'error' || kind === 'warning' ? AUTO_CLOSE_ERROR_MS : AUTO_CLOSE_MS);
    const { toast, closeButton } = render({ type: kind, title, text, action, timeout });
    toast.dataset.key = key;
    let timer = null;

    const close = () => {
        if (toast.classList.contains('is-leaving')) {
            return;
        }
        clearTimeout(timer);
        toast.classList.add('is-leaving');
        setTimeout(() => {
            toast.remove();
            if (onClose) {
                onClose();
            }
        }, LEAVE_MS);
    };

    const show = () => {
        container().appendChild(toast);
        replayAnimations(toast);
        clearTimeout(timer);
        if (timeout) {
            timer = setTimeout(close, timeout);
        }
    };

    closeButton.addEventListener('click', close);
    toast.addEventListener(RESHOW_EVENT, show);
    show();
    return toast;
}

/**
 * Drop-in replacement for vue-toasted: keeps this.$toasted.show(message, { type, duration, onComplete })
 * and this.$toasted.error(...) working, rendered as site toasts.
 */
export const SiteToasts = {
    install(Vue) {
        const show = (message, { type, duration, onComplete } = {}) => showToast({
            type,
            title: message,
            duration,
            onClose: onComplete,
        });
        Vue.prototype.$toasted = {
            show,
            error: (message, options = {}) => show(message, { ...options, type: 'error' }),
            success: (message, options = {}) => show(message, { ...options, type: 'success' }),
            info: (message, options = {}) => show(message, { ...options, type: 'info' }),
        };
    },
};
